import React, { useState } from 'react';
import { Modal, Form, Input, Select, Button, Alert, Typography, Space } from 'antd';
import { UserAddOutlined, LoadingOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { apiTokenRequest, apiConfig } from '../authConfig';
import { useApp } from '../context/AppContext';

const { Text, Title } = Typography;

/**
 * Invite New Identity modal.
 * Collects name, email, role and calls POST /api/v1/invite on the backend.
 * Now powered by Ant Design Modal + Form.
 */
const InviteModal = ({ onClose, onInviteSuccess, roles = [], projectName = '' }) => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    const [form] = Form.useForm();
    const { user, activeProject } = useApp();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleSubmit = async (values) => {
        setLoading(true);
        setResult(null);

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...apiTokenRequest,
                account: activeAccount,
            });

            const response = await fetch(`${apiConfig.baseUrl}/api/v1/invite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${tokenResponse.accessToken}`,
                },
                body: JSON.stringify({ ...values, projectId: activeProject?.id }),
            });

            const json = await response.json().catch(() => ({}));

            if (!response.ok || !json.success) {
                const msg =
                    json.error?.message ||
                    (response.status === 409
                        ? 'This email is already assigned a different role. Use the same role or another email.'
                        : `Invitation failed (${response.status})`);
                throw new Error(msg);
            }

            // QE-03: the server reports a partial success (user added, invite email not sent) explicitly.
            const warnings = Array.isArray(json.data?.warnings) ? json.data.warnings : [];
            setResult(
                warnings.length > 0
                    ? { type: 'warning', message: `${json.data.message} ${warnings.join(' ')}` }
                    : {
                        type: 'success',
                        message: json.data.message || `${values.displayName} has been successfully invited.`,
                    },
            );
            form.resetFields();
            if (onInviteSuccess) onInviteSuccess();
        } catch (error) {
            console.error('Invite failed:', error);
            setResult({
                type: 'error',
                message: error.message || 'Failed to provision authorization link.',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={true}
            onCancel={onClose}
            footer={null}
            title={<Title level={4} style={{ margin: 0 }}>Invite New Identity</Title>}
            width={520}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{ role: roles[0]?.value || '' }}
                style={{ marginTop: 16 }}
            >
                <Form.Item
                    name="displayName"
                    label={<Text style={{ fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}>IDENTITY NAME</Text>}
                    rules={[{ required: true, message: 'Please enter a name' }]}
                >
                    <Input placeholder="e.g. John Doe" size="large" />
                </Form.Item>

                <Form.Item
                    name="email"
                    label={<Text style={{ fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}>COMMUNICATION EMAIL</Text>}
                    rules={[
                        { required: true, message: 'Please enter an email' },
                        { type: 'email', message: 'Please enter a valid email' },
                    ]}
                >
                    <Input placeholder="name@company.com" size="large" />
                </Form.Item>

                <Form.Item
                    name="role"
                    label={<Text style={{ fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', color: 'var(--color-text-muted)' }}>PERMISSION PROTOCOL</Text>}
                    rules={[{ required: true, message: 'Please select a role' }]}
                >
                    {String(user?.role || '').toUpperCase() === 'COMPANY_SPOC' ? (
                        <div style={{
                            padding: '12px 20px',
                            background: 'var(--color-accent-tint)',
                            border: '1px solid var(--color-info-line)',
                            borderRadius: '12px',
                            fontSize: '14px',
                            fontWeight: 900,
                            color: 'var(--color-accent)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em'
                        }}>
                            Company Delegate
                        </div>
                    ) : (
                        <Select
                            size="large"
                            options={roles.map((role) => ({
                                value: role.value,
                                label: role.label,
                            }))}
                        />
                    )}
                </Form.Item>

                <Alert
                    message={
                        String(user?.role || '').toUpperCase() === 'COMPANY_SPOC'
                            ? 'Authorized SPOC privileges restricted to provision company delegates only.'
                            : `Security Compliance: You can provision ${roles.map(r => r.label).join(' and ')} identities.`
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 20, fontSize: 12 }}
                />

                <Button
                    type="primary"
                    htmlType="submit"
                    block
                    size="large"
                    loading={loading}
                    icon={!loading && <UserAddOutlined />}
                    style={{
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        height: 48,
                        borderRadius: 10,
                    }}
                >
                    PROVISION AUTHORIZATION LINK
                </Button>

                {result && (
                    <Alert
                        message={result.message}
                        type={result.type}
                        showIcon
                        icon={result.type === 'success' ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
                        style={{ marginTop: 16 }}
                    />
                )}
            </Form>
        </Modal>
    );
};

export default InviteModal;
